/**
 * @fileoverview Shared "open an emailed-code challenge and navigate" helpers.
 * @description Every flow that ends in an emailed code stores the challenge and navigates to
 * /two-factor, whose form verifies it against the right endpoint for its purpose:
 * openTwoFactorChallenge handles a POST /identity/login response (absolute ISO timestamps; its
 * nextStep says whether it's a login code or, for a registered-but-not-activated account, an
 * activation code), and openActivationChallenge handles POST /identity/invitations/accept
 * (relative `*InSeconds` timings and `maskedDestination` — toActivationChallenge is shared with
 * the activation resend). Both normalize to epoch milliseconds, so no hook duplicates that logic.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { ROUTES } from '@/shared/constants/routes.constants'

import type { TwoFactorChallenge } from '@/shared/lib/auth/auth.types'
import type { useTwoFactorChallenge } from '@/shared/lib/auth/useTwoFactorChallenge'
import type { NavigateFunction } from 'react-router'

/**
 * @description The subset of POST /identity/login's response this helper needs. A structural
 * type rather than importing features/auth's own LoginResponse: shared may not depend on a
 * feature (ADR-0002), and every field here is already the login endpoint's real response shape.
 */
export interface OpenedTwoFactorChallenge {
  challengeId: string
  destinationMasked: string
  expiresAt: string
  resendAvailableAt: string
  nextStep?: 'VERIFY_LOGIN_MFA' | 'VERIFY_ACTIVATION_MFA'
}

/**
 * @description The activation challenge shape POST /identity/invitations/accept and POST
 * /identity/activation/mfa/resend return (structural, for the same ADR-0002 reason as above).
 */
export interface OpenedActivationChallenge {
  challengeId: string
  maskedDestination: string
  expiresInSeconds: number
  resendAvailableInSeconds: number
}

/**
 * @description Converts an activation challenge's relative timings into a stored challenge.
 * @param {OpenedActivationChallenge} response - The accept/resend endpoint's response.
 * @param {string} email - The address to carry forward ('' when unknown).
 * @returns {TwoFactorChallenge} The challenge to store.
 */
export function toActivationChallenge(
  response: OpenedActivationChallenge,
  email: string,
): TwoFactorChallenge {
  const now = Date.now()

  return {
    challengeId: response.challengeId,
    purpose: 'ACTIVATION',
    email,
    destinationMasked: response.maskedDestination,
    expiresAt: now + response.expiresInSeconds * 1000,
    resendAvailableAt: now + response.resendAvailableInSeconds * 1000,
  }
}

/**
 * @description Stores the two-factor challenge a successful login just opened and navigates to
 * the verification page.
 * @param {ReturnType<typeof useTwoFactorChallenge>['setChallenge']} setChallenge - The pending
 * two-factor challenge store's setter.
 * @param {NavigateFunction} navigate - The router's navigate function.
 * @param {string} email - The email address the challenge was opened for.
 * @param {OpenedTwoFactorChallenge} response - The login endpoint's response.
 */
export function openTwoFactorChallenge(
  setChallenge: ReturnType<typeof useTwoFactorChallenge>['setChallenge'],
  navigate: NavigateFunction,
  email: string,
  response: OpenedTwoFactorChallenge,
): void {
  setChallenge({
    challengeId: response.challengeId,
    purpose: response.nextStep === 'VERIFY_ACTIVATION_MFA' ? 'ACTIVATION' : 'LOGIN',
    email,
    destinationMasked: response.destinationMasked,
    expiresAt: new Date(response.expiresAt).getTime(),
    resendAvailableAt: new Date(response.resendAvailableAt).getTime(),
  })
  void navigate(ROUTES.TWO_FACTOR, { replace: true })
}

/**
 * @description Stores the activation challenge an accepted invitation just opened and navigates to
 * the code-entry page.
 * @param {ReturnType<typeof useTwoFactorChallenge>['setChallenge']} setChallenge - The pending
 * challenge store's setter.
 * @param {NavigateFunction} navigate - The router's navigate function.
 * @param {OpenedActivationChallenge} response - The accept endpoint's response.
 */
export function openActivationChallenge(
  setChallenge: ReturnType<typeof useTwoFactorChallenge>['setChallenge'],
  navigate: NavigateFunction,
  response: OpenedActivationChallenge,
): void {
  setChallenge(toActivationChallenge(response, ''))
  void navigate(ROUTES.TWO_FACTOR, { replace: true })
}
